import React from 'react';

import { CTAButtonV2 } from '../../components/ctabutton';
import { getLanguageField } from '../../utils/language';
import SiteContainer from 'src/components/siteContainer/siteContainer';
import { ModuleHeadV2 } from 'src/components/moduleHead/moduleHead.v2';
import { ModuleWrapperV2 } from 'src/components/moduleWrapper/moduleWrapper.v2';
import BackgroundImageWrapper from 'src/components/backgroundImageWrapper/backgroundImageWrapper';

export default function TextBlock(props) {
  const {data} = props;

  if(!data || (data && !data.item)) {
    return null;
  }

  const parseAlignment = (alignment) => {
    if(alignment === "text-center") return `${alignment} flex flex-col items-center`
    if(alignment === "text-left") return `${alignment} flex flex-col items-start`
    if(alignment === "text-right") return `${alignment} flex flex-col items-end`
    return "";
  }

  function getBackgroundImageProps(wrapper){
    const { background_image, background_image_placement, overlay, background_color_overlay, background_overlay_opacity } = data.item;

    const overlayColor = overlay && background_color_overlay?.value && background_overlay_opacity ?
    background_color_overlay.value +  Math.floor( background_overlay_opacity / 100 * 255 || 0 * 255).toString(16)
    : "";

    const props = {
      backgroundImage: background_image?.imageFile,
      overlayColor
    }
    if(wrapper === "background-image-wrapper" && (background_image_placement === "full_screen" || data?.item?.width === "full_screen")) return props;
    if(wrapper === "module-wrapper" && background_image_placement === "boxed") return props;
    return {};
  }

  const moduleData = {
    title: getLanguageField(data.item.translations, data.selectedLanguage, 'title'),
    subtitle: getLanguageField(data.item.translations, data.selectedLanguage, 'subtitle'),
    description: getLanguageField(data.item.translations, data.selectedLanguage, 'body', true, true),
    alignment: parseAlignment(data.item?.alignment),
    display: data.item.display || 'one_column',
    titlePosition: data.item.title_position || 'left',
    anchor: getLanguageField(data?.item?.translations, data.selectedLanguage, 'anchor'),
    name: data?.item?.name,
    backgroundImage: data?.item?.background_image?.imageFile,
    fullScreen: data.item.width === 'full_screen',
    desktopWidth: data.item?.desktop_width,
    textPosition: data.item?.text_position
  };

  let colors = {
    background: data.item.background_color?.value,
    boxedBackground: !moduleData.fullScreen ? data.item.boxed_background_color?.value : "",
    title: data.item.title_color?.value,
    subtitle: data.item.subtitle_color?.value,
    description: data.item.body_color?.value,
  }

  const roundedCorners = data.item.width !== 'full_screen' && data.item.rounded_corners === true;

  const innerPadding = data.item.width !== 'full_screen' ? 'px-4 sm:px-6 lg:px-8 py-8' : 'px-6 xl:px-0';

  const order = moduleData.titlePosition === 'right' ? 'order-last md:order-first' : '';

  const outterMargin = data.item.width !== 'full_screen' ? 'mx-4 sm:mx-6 xl:mx-0 my-8 lg:my-12' : '';

  const getWidthClassname = () => {
    const { desktopWidth } = moduleData;
    if (desktopWidth === "2/3") return "lg:w-2/3";
    if (desktopWidth === "1/2") return "lg:w-1/2";
    return "";
  }

  const getAlignmentClassname = () => {
    const { textPosition, desktopWidth } = moduleData;
    if (desktopWidth === "full_width") return "";
    if (textPosition === "center") return "items-center";
    if (textPosition === "left") return "items-start";
    if (textPosition === "right") return "items-end";
    return ""
  }

  const RenderCTA = ({cta}) => {
    if (!cta) return null;
    return <div className="flex">
      <CTAButtonV2
        className="w-full md:w-auto"
        language={data.selectedLanguage}
        defaultLanguage={data.defaultLanguage}
        ctabutton={cta} />
    </div>;
  }

  //render only title and subtitle or just description
  const RenderHead = ({ title, subtitle, description }) =>
    <ModuleHeadV2
      title={title}
      subtitle={subtitle}
      description={description}
      color_title={colors.title}
      color_subtitle={colors.subtitle}
      color_description={colors.description}
      alignment={moduleData.alignment}
      align_lists
    />

  // different display options
  const SingleColumn = () => {
    const width = getWidthClassname();
    const alignment = getAlignmentClassname();
    return <div className={`max-w-7xl flex flex-col ${alignment} mx-auto`}>
      <div className={`flex ${width} flex-col`}>
        <RenderHead title={moduleData.title} subtitle={moduleData.subtitle} description={moduleData.description}/>
        <div className={`${moduleData.alignment}`}>
          <div className='flex gap-4 items-center'>
            <RenderCTA cta={data.item.primary_cta}/>
            <RenderCTA cta={data.item.secondary_cta}/>
            <RenderCTA cta={data.item.soft_cta}/>
          </div>
        </div>
      </div>
    </div>
  }

  //two columns divides textblock in two columns(desktop) and applies title, subtitle in one column and description in the other
  const TwoColumns = () =>
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6">
      <div className={`${moduleData.alignment} flex-0 md:flex-1`}>
        <div className={`hidden md:block`}>
          <RenderHead title={moduleData.title} subtitle={moduleData.subtitle}/>
        </div>
        <div className={`block md:hidden`}>
          <RenderHead/>
        </div>
      </div>
      <div className={`${moduleData.alignment} flex-0 md:flex-1 ${order}`}>
        <RenderHead description={moduleData.description}/>
        <div className='flex gap-4 items-center'>
          <RenderCTA cta={data.item.primary_cta}/>
          <RenderCTA cta={data.item.secondary_cta}/>
          <RenderCTA cta={data.item.soft_cta}/>
        </div>
      </div>
    </div>

  const style = {
    '--hyperlink-text-color': data.item.hyperlink_text_color?.value,
    '--hyperlink-hover-color': data.item.hyperlink_hover_color?.value,
  }

  return (
    <BackgroundImageWrapper {...getBackgroundImageProps("background-image-wrapper")} backgroundColor={colors.background}>
      <SiteContainer fullwidth={moduleData.fullScreen} style={style}>
        <ModuleWrapperV2
          isFullWidth={moduleData.fullScreen}
          roundedCorners={roundedCorners}
          anchor={moduleData.anchor}
          name={moduleData.name}
          className={`relative ${innerPadding} ${outterMargin}`}
          paddings={{top: data.item.padding_top, bottom: data.item.padding_bottom}}
          backgroundColor={colors.boxedBackground}
          {...getBackgroundImageProps("module-wrapper")}
        >
          {moduleData.display === 'two_columns' ? <TwoColumns /> : <SingleColumn />}
        </ModuleWrapperV2>
      </SiteContainer>
    </BackgroundImageWrapper>
  )
}